import { useParams } from "react-router";
import t from "@/lang/lang";
import {
  FloatNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MaterialPriceListComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/material-price-list";
import { useCalculationData } from "@/services/backend/qpm/calculations/hooks/calculation-context";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { useQpmCalculationUpdateMaterialAdditionPriceMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import type {
  MaterialAdditionPrice,
  UpdateMaterialAdditionPriceRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useEffect, useState } from "react";

export function MaterialRoute() {
  const { calculationId } = useParams();
  const { calculation, error } = useCalculationData();

  const displayMaterial = [
    MonitorType.Checkpoint,
    MonitorType.FlyExterminator,
    MonitorType.IoT,
  ];
  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }
  return (
    calculationId && (
      <div className="mb-12 overflow-clip rounded-lg border shadow-sm">
        <Accordion
          type="multiple"
          defaultValue={[...displayMaterial, "additions"]}
        >
          {displayMaterial.map((key) => (
            <MaterialCostsComponent
              key={key}
              calculationId={calculationId}
              monitorTypeKey={key}
            />
          ))}
          <AccordionItem value="additions" className="rounded-none">
            <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
              {t("Materialkosten Zusatzmaterial")}
            </AccordionTrigger>
            <AccordionContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Zusatzmaterial")}</TableHead>
                    <TableHead className="text-right">{t("Anzahl")}</TableHead>
                    <TableHead className="text-right">
                      {t("Preis pro Stück")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("Gesamtpreis")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calculation?.materialAdditionCost?.positions?.map(
                    (position) => (
                      <MaterialAdditionPriceRow
                        key={`${position.parentMonitorId}-${position.additionalId}`}
                        calculationId={calculationId}
                        position={position}
                      />
                    ),
                  )}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    )
  );
}

type MaterialAdditionPriceRowProps = {
  calculationId: string;
  position: MaterialAdditionPrice;
};

function MaterialAdditionPriceRow({
  calculationId,
  position,
}: MaterialAdditionPriceRowProps) {
  const [price, setPrice] = useState(position.basePrice);
  const [request, setRequest] = useState<UpdateMaterialAdditionPriceRequest>({
    calculationId,
    parentMonitorId: position.parentMonitorId,
    additionalId: position.additionalId,
    basePrice: position.basePrice,
  });
  const [setMaterialAdditionPrice, { isLoading, error, reset, isSuccess }] =
    useQpmCalculationUpdateMaterialAdditionPriceMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setMaterialAdditionPrice,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  useEffect(() => {
    setPrice(position.basePrice);
  }, [position.basePrice]);

  return (
    <TableRow>
      <TableCell className="py-2 font-medium">{position.name}</TableCell>
      <TableCell className="py-2 text-right">{position.amount}</TableCell>
      <TableCell className="py-2 text-right">
        <FloatNumberInput
          className="justify-self-end"
          value={price}
          fractions={2}
          onChange={(value) => {
            setPrice(value);
            setRequest({
              calculationId,
              parentMonitorId: position.parentMonitorId,
              additionalId: position.additionalId,
              basePrice: value,
            });
          }}
          disabled={isLoading}
        />
      </TableCell>
      <TableCell className="py-2 text-right">
        <Value value={position.totalPrice} />
      </TableCell>
    </TableRow>
  );
}

export type MaterialCostsComponentProps = {
  calculationId: string;
  monitorTypeKey: MonitorType;
};

function MaterialCostsComponent({
  calculationId,
  monitorTypeKey,
}: MaterialCostsComponentProps) {
  const { calculation, error } = useCalculationData();
  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const totalCost = calculation?.materialCost?.[monitorTypeKey].totalCost || 0;
  return (
    <AccordionItem value={monitorTypeKey} className="rounded-none">
      <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
        {t("Materialkosten")} {t(MonitorTypeUtil.toString(monitorTypeKey))}
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-t">
          <div className="">
            <MaterialPriceListComponent
              calculationId={calculationId}
              materialPriceList={
                calculation?.materialCost?.[monitorTypeKey] ?? undefined
              }
              monitors={calculation?.monitorSnaps ?? undefined}
              totalCost={totalCost}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
